import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import {
  EnterpriseFeatureConfig,
  EnterpriseFeatureConfigService,
} from 'app/services/enterprise-feature-config.service';
import { EnterpriseService } from 'app/services/enterprise.service';
import { SecurityProfileService } from 'app/services/securityProfile.service';
import { UserService } from 'app/services/user.service';
import { ROLES } from 'shared-models';
import { canUserTrackTimeInEnterprise } from '../timerecord-role.util';

/**
 * A dónde va cada rol cuando no se le puede dejar donde pedía.
 *
 * ⚠️ **Estaba escrito tres veces en este fichero** —el arranque, el caso especial de
 * `/landing/home` y el respaldo final— con los mismos `switch` copiados. Tres copias de un
 * mapa de destinos es exactamente cómo se fabrica un bucle de redirección: basta con
 * actualizar dos.
 *
 * ⚠️ **Y había una CUARTA fuera de aquí**, en `compact.component.navigateAfterSwitch()`, con
 * el destino a pelo: `ADMIN ? '/enterprise-dashboard' : '/landing/home'`. Al cambiar a una
 * empresa sin control horario mandaba al dashboard, que está capado, y el otro guard lo
 * rebotaba al panel. Se llegaba al sitio correcto por accidente, dando un rebote, y a un
 * administrador que además ficha se le robaba su home. Por eso esto se exporta: el mapa vive
 * en un único sitio y quien navegue por su cuenta lo reusa.
 *
 * ⚠️ **Y el bucle existía de verdad.** Sin control horario, `/reports` queda capada; AUDITOR
 * pedía `/landing/home`, de aquí salía hacia `/reports`, y `ModuleFeatureGuard` lo devolvía a
 * `/landing/home` por no ser admin. Infinito. Se rompe aquí, que es donde se decide el
 * destino, y no en el mapa de respaldo del otro guard.
 *
 * ⚠️ **Se lee el snapshot de forma SÍNCRONA, y si no está cargado se hace lo de siempre.**
 * `RoleGuard` es `canActivateChild` de casi toda la aplicación; volverlo asíncrono para
 * esperar a `ensureLoaded()` es un cambio de radio enorme. El precio es un parpadeo en frío
 * —un empleado solo-Planner puede ver la home un instante antes de saltar a sus turnos—, y
 * es el intercambio correcto: equivocarse hacia el comportamiento de hoy no rompe nada.
 */
export const landingUrlFor = (
  role: ROLES | null,
  features: EnterpriseFeatureConfig,
  canTrackTime: boolean
): string => {
  const timeRecords = !features.loaded || features.timeRecordsEnabled;
  const reports = !features.loaded || features.reportsEnabled;

  switch (role) {
    case ROLES.ADMIN:
      /**
       * `canTrackTime` manda por encima del módulo, y es el orden que importa.
       *
       * ⚠️ **Es el interruptor «Puede fichar» de la ficha del empleado, y se activa por
       * persona**, no por empresa: un administrador que además trabaja como empleado lo tiene
       * puesto, tenga la empresa control horario o no. `navigation.service` ya le enseña Home
       * y Solicitudes por ese mismo atajo.
       *
       * ⚠️ **Cortar aquí por `!timeRecords` antes de mirarlo dejaba el menú mintiendo**: le
       * aparecía Home y al pulsarla rebotaba al panel. Un enlace muerto en su propio menú, sin
       * error y sin nada que lo explique.
       *
       * Y la home le sirve igual sin fichaje: conserva vacaciones, saldos y calendario.
       */
      if (canTrackTime) return '/landing/home';
      // Sin fichaje el dashboard de empresa está capado, así que su sitio es el panel.
      return timeRecords ? '/enterprise-dashboard' : '/admin/panel';
    case ROLES.GESTOR:
      return '/admin/panel';
    case ROLES.AUDITOR:
      /**
       * ⚠️ **AUDITOR no se vende en Planner-solo**, y aquí es donde se nota: sin informes no
       * le queda ninguna pantalla suya. Se le deja en la home en vez de rebotarlo, que es lo
       * único que no forma bucle, y queda avisado en consola para que no sea silencioso.
       */
      if (!reports) {
        console.warn('[RoleGuard] AUDITOR sin informes: esta empresa no debería tener ese rol.');
        return '/landing/home';
      }
      return '/reports';
    case ROLES.CONFIG_ADMIN:
      return '/configuration/panel';
    case ROLES.SUPERADMIN:
      // Interno: siempre la home, que no se capa.
      return '/landing/home';
    default:
      // USER y MANAGER. Sin fichaje, su pantalla es su cuadrante — si lo hay.
      return !timeRecords && features.plannerEnabled ? '/mis-turnos' : '/landing/home';
  }
};

export const RoleGuard: CanActivateFn | CanActivateChildFn = (route, state) => {
  const router: Router = inject(Router);
  const securityProfileService = inject(SecurityProfileService);
  const userService = inject(UserService);
  const enterpriseService = inject(EnterpriseService);
  const userRole = securityProfileService.getRole();
  const canTrackTime = canUserTrackTimeInEnterprise(
    userService.getUser(),
    enterpriseService.selectedEnterprise?.id
  );
  // Síncrono a propósito: ver `landingUrlFor`.
  const features = inject(EnterpriseFeatureConfigService).getSnapshot();

  if (state.url.startsWith('/configuration') && userRole !== ROLES.CONFIG_ADMIN && userRole !== ROLES.SUPERADMIN) {
    return router.parseUrl('/landing/home');
  }

  // Handle root path redirects based on role
  if (state.url === '/' || state.url === '/signed-in-redirect' || state.url === '/auth-check') {
    return router.parseUrl(landingUrlFor(userRole, features, canTrackTime));
  }

  // Special case: if trying to access /landing/home but role is restricted, redirect
  if (state.url === '/landing/home') {
    if (
      userRole === ROLES.ADMIN ||
      userRole === ROLES.GESTOR ||
      userRole === ROLES.AUDITOR ||
      userRole === ROLES.CONFIG_ADMIN
    ) {
      const destino = landingUrlFor(userRole, features, canTrackTime);
      // ⚠️ Si su sitio ES la home, se le deja. Rebotar a quien ya está donde le toca es
      // justo la forma del bucle de AUDITOR.
      return destino === '/landing/home' ? true : router.parseUrl(destino);
    }
  }

  if (userRole === ROLES.SUPERADMIN) {
    return true;
  }

  if (userRole === ROLES.CONFIG_ADMIN && !state.url.startsWith('/configuration')) {
    return router.parseUrl('/configuration/panel');
  }

  const mergedRouteData = route.pathFromRoot
    .map((r) => r.data)
    .reduce(
      (acc, data) => ({
        ...acc,
        ...data,
      }),
      {}
    );

  const allowedRoles: ROLES[] = mergedRouteData['roles'];

  if (!allowedRoles || !allowedRoles.length) {
    return router.parseUrl('/landing/home');
  }

  if (userRole && allowedRoles.includes(userRole)) {
    return true;
  }

  if (userRole === ROLES.ADMIN && canTrackTime) {
    const canEnterTrackableRoute = allowedRoles.includes(ROLES.USER) || allowedRoles.includes(ROLES.MANAGER);
    if (canEnterTrackableRoute) {
      return true;
    }
  }

  // If user doesn't have permission, redirect to their appropriate landing page
  return router.parseUrl(landingUrlFor(userRole, features, canTrackTime));
};
